import React from "react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import WhatsAppFloat from "@/components/sections/WhatsAppFloat";
import { Button } from "@/components/ui/button";
import {
  Wallet,
  RefreshCw,
  Sparkles,
  GraduationCap,
  Stethoscope,
  Home as HomeIcon,
  Briefcase,
  TrendingUp,
  Users,
  Building2,
  ArrowRight,
  MessageCircle,
  ShieldCheck,
  Scale,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useLeadDialog } from "@/components/sections/LeadDialog";

const personas = [
  { icon: Wallet, title: "Crédito de consumo", desc: "Compara opciones desde $500.000 CLP para tus gastos cotidianos o proyectos personales." },
  { icon: Sparkles, title: "Crédito de libre disposición", desc: "Un préstamo personal flexible para usar como prefieras, con plazos desde 61 días." },
  { icon: RefreshCw, title: "Refinanciamiento de deudas", desc: "Reúne varias deudas en un solo crédito con una cuota mensual más ordenada." },
  { icon: HomeIcon, title: "Remodelación del hogar", desc: "Financia mejoras, ampliaciones o reparaciones en tu vivienda." },
  { icon: GraduationCap, title: "Crédito para estudios", desc: "Cubre matrículas, diplomados o cursos técnicos en Chile." },
  { icon: Stethoscope, title: "Gastos médicos", desc: "Solicita un crédito para tratamientos, intervenciones o emergencias de salud." },
];

const empresas = [
  { icon: TrendingUp, title: "Capital de trabajo", desc: "Liquidez para inventario, operación y crecimiento de tu Pyme." },
  { icon: Briefcase, title: "Financiamiento para inversión", desc: "Para maquinaria, equipamiento o expansión comercial." },
  { icon: Users, title: "Convenios corporativos", desc: "Beneficio para tus colaboradores: acceso a créditos vía convenio con Bancoorp." },
  { icon: Building2, title: "Asesoría financiera", desc: "Un ejecutivo analiza tu necesidad y te conecta con la entidad adecuada." },
];

const promesas = [
  { icon: Scale, title: "100% comparativo", desc: "Recibe ofertas de distintas entidades financieras en un mismo proceso." },
  { icon: ShieldCheck, title: "Sin costo para ti", desc: "Comparar opciones con Bancoorp es gratuito. Solo pagas el crédito que contrates." },
  { icon: Sparkles, title: "Transparencia total", desc: "CAE, cuotas y costo total claros, entregados por la entidad financiera." },
];

export default function Servicios() {
  const { open } = useLeadDialog();
  const openLead = () => open("servicios");

  return (
    <div className="min-h-screen bg-white" data-testid="servicios-page">
      <Header onCtaClick={openLead} />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24 border-b border-slate-200">
          <div
            className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl -translate-y-1/3 translate-x-1/3"
            style={{ backgroundColor: "#2DD4BF" }}
            aria-hidden="true"
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600">
              Servicios
            </span>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Soluciones de crédito
              <span className="block text-blue-600">para personas y empresas</span>
            </h1>
            <p className="mt-6 max-w-3xl text-lg text-slate-600 leading-relaxed">
              Bancoorp conecta a personas y empresas en Chile con entidades
              financieras aliadas. Compara opciones, recibe ofertas claras y
              elige la mejor alternativa para ti.
            </p>

            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 max-w-2xl">
              <strong>Importante:</strong> Bancoorp actúa como intermediario y
              no otorga directamente los créditos. La aprobación y condiciones
              finales dependen de la entidad financiera.
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                onClick={openLead}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-7 py-6 text-base font-semibold"
                data-testid="servicios-hero-cta"
              >
                Solicitar evaluación
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="rounded-full px-7 py-6 text-base border-slate-300"
              >
                <Link to="/como-funciona">¿Cómo funciona?</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Personas */}
        <section className="py-20 lg:py-24" id="personas">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
              <div className="max-w-2xl">
                <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600">
                  Para personas
                </span>
                <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                  Créditos para tu vida y proyectos
                </h2>
                <p className="mt-3 text-slate-600">
                  Desde $500.000 CLP, plazos desde 61 días y tasa referencial
                  desde 1% mensual.
                </p>
              </div>
              <Button
                onClick={openLead}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-5 self-start lg:self-auto"
                data-testid="servicios-personas-cta"
              >
                Comparar opciones
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {personas.map((s, i) => (
                <article
                  key={i}
                  className="rounded-2xl bg-slate-50 border border-slate-200 p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:border-blue-200"
                  data-testid={`personas-card-${i}`}
                >
                  <div className="h-10 w-10 rounded-lg bg-blue-600 text-white flex items-center justify-center mb-4">
                    <s.icon className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <h3 className="text-base font-semibold text-slate-900">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {s.desc}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Empresas */}
        <section className="py-20 bg-slate-50 border-y border-slate-200" id="empresas">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
              <div className="max-w-2xl">
                <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600">
                  Para empresas
                </span>
                <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                  Financiamiento para tu Pyme
                </h2>
                <p className="mt-3 text-slate-600">
                  Soluciones referenciales desde $3M hasta $150M CLP, según
                  perfil y entidad evaluadora.
                </p>
              </div>
              <Button
                onClick={openLead}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 py-5 self-start lg:self-auto"
                data-testid="servicios-empresas-cta"
              >
                Hablar con un ejecutivo
                <MessageCircle className="h-4 w-4 ml-2" />
              </Button>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {empresas.map((s, i) => (
                <article
                  key={i}
                  className="rounded-2xl bg-white border border-slate-200 p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:border-blue-200"
                  data-testid={`empresas-card-${i}`}
                >
                  <div className="h-10 w-10 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center mb-4">
                    <s.icon className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <h3 className="text-base font-semibold text-slate-900">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {s.desc}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Promesa */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600">
                Nuestra promesa
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                Una sola solicitud, múltiples opciones
              </h2>
            </div>

            <div className="mt-10 grid sm:grid-cols-3 gap-5">
              {promesas.map((p, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-200 bg-white p-6"
                  data-testid={`promesa-${i}`}
                >
                  <div className="h-10 w-10 rounded-lg bg-blue-600 text-white flex items-center justify-center mb-4">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="py-20 bg-slate-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Empieza tu evaluación gratuita
            </h2>
            <p className="mt-4 text-slate-300 max-w-2xl mx-auto">
              Completa el formulario y un asesor te contactará por WhatsApp con
              las opciones disponibles. Sin costo y sin compromiso.
            </p>
            <Button
              onClick={openLead}
              className="mt-8 bg-teal-400 hover:bg-teal-300 text-slate-900 font-semibold rounded-full px-7 py-6 text-base"
              data-testid="servicios-bottom-cta"
            >
              Solicitar opciones
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <p className="mt-5 text-xs text-slate-400 max-w-xl mx-auto">
              Bancoorp actúa como intermediario y no otorga directamente los
              créditos. Las condiciones finales son entregadas por la entidad
              financiera.
            </p>
          </div>
        </section>
      </main>
      <Footer onCtaClick={openLead} />
      <WhatsAppFloat />
    </div>
  );
}
